import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { palette, useThemePalette } from '@/config/theme';
import type { MatchProbabilities } from '@/features/predictions/model';
import type { TeamRef } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

const FORM_TONE: Record<string, string> = { W: palette.win, D: palette.draw, L: palette.loss };

/** Last-five result pips (W/D/L), tinted; nothing if no form is known. */
function FormPips({ form }: { form: string | null }) {
  const palette = useThemePalette();
  if (!form) return null;
  return (
    <View className="flex-row gap-1">
      {form.split('-').map((letter, index) => {
        const tone = FORM_TONE[letter] ?? palette.muted;
        return (
          <View
            key={index}
            className="h-4 w-4 items-center justify-center rounded-full"
            style={{ backgroundColor: `${tone}26` }}
          >
            <AppText weight="bold" style={{ fontSize: 9, color: tone }}>
              {letter}
            </AppText>
          </View>
        );
      })}
    </View>
  );
}

function DriverRow({
  team,
  form,
  ppg,
  align,
}: {
  team: TeamRef | null;
  form: string | null;
  ppg: number | null;
  align: 'start' | 'end';
}) {
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  return (
    <View className={`flex-1 gap-1.5 ${align === 'end' ? 'items-end' : 'items-start'}`}>
      <AppText variant="micro" weight="bold" className="text-ink" numberOfLines={1}>
        {localizeTeamName(team, locale, true)}
      </AppText>
      <FormPips form={form} />
      {ppg != null ? (
        <AppText variant="micro" className="text-muted" tabular>
          {formatNumber(Number(ppg.toFixed(2)), numeralOptions)} {t('match.ppgShort')}
        </AppText>
      ) : null}
    </View>
  );
}

/**
 * "Model Insight" - the form model's read, moved INSIDE the match (was a home
 * card). A natural-language verdict, the proportional 1X2 bar, and the drivers
 * behind it (recent form + points-per-game) for each side. Labelled beta; it is
 * a transparent points-per-game heuristic, not betting odds.
 */
export function ModelInsight({
  probabilities,
  homeTeam,
  awayTeam,
  homeForm,
  awayForm,
  homePpg,
  awayPpg,
}: {
  probabilities: MatchProbabilities;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
  homeForm: string | null;
  awayForm: string | null;
  homePpg: number | null;
  awayPpg: number | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();

  const topPct = Math.max(probabilities.home, probabilities.draw, probabilities.away);
  const confident = topPct >= 50;
  const pickName =
    probabilities.pick === 'home'
      ? localizeTeamName(homeTeam, locale, true)
      : localizeTeamName(awayTeam, locale, true);
  const verdict =
    probabilities.pick === 'draw'
      ? t('match.insightEven')
      : t('match.insightFavoured', { team: pickName });

  const segments = [
    { pct: probabilities.home, color: palette.win, label: localizeTeamName(homeTeam, locale, true) },
    { pct: probabilities.draw, color: palette.draw, label: t('matches.drawShort') },
    { pct: probabilities.away, color: palette.loss, label: localizeTeamName(awayTeam, locale, true) },
  ];

  return (
    <Card className="mx-4 mb-4 gap-3 px-4 py-3.5">
      <View className="flex-row items-center gap-2">
        <Ionicons name="sparkles" size={14} color={palette.volt} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-ink">
          {t('match.modelInsight')}
        </AppText>
        <View className="flex-1" />
        <AppText variant="micro" className="text-soft">
          {t('match.modelBeta')}
        </AppText>
      </View>

      <View className="flex-row items-center gap-2 rounded-card border border-volt-line bg-volt-glass px-3 py-2">
        <AppText variant="caption" weight="semibold" className="flex-1 text-ink">
          {verdict}
        </AppText>
        <View className="rounded-full bg-surface-2 px-2 py-0.5">
          <AppText variant="micro" weight="bold" className="text-soft">
            {confident ? t('match.insightStrong') : t('match.insightSlight')}
          </AppText>
        </View>
      </View>

      {/* 1X2 bar */}
      <View className="h-2.5 flex-row overflow-hidden rounded-full bg-inset">
        {segments.map((segment, index) => (
          <View key={index} style={{ flex: Math.max(segment.pct, 1), backgroundColor: segment.color }} />
        ))}
      </View>
      <View className="flex-row items-start">
        {segments.map((segment, index) => (
          <View
            key={index}
            className={`flex-1 gap-0.5 ${
              index === 0 ? 'items-start' : index === 1 ? 'items-center' : 'items-end'
            }`}
          >
            <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
              {segment.label}
            </AppText>
            <AppText variant="caption" weight="bold" tabular style={{ color: segment.color }}>
              {formatNumber(segment.pct, numeralOptions)}%
            </AppText>
          </View>
        ))}
      </View>

      {/* Drivers behind the read */}
      {homeForm || awayForm || homePpg != null || awayPpg != null ? (
        <View className="mt-1 flex-row items-start border-t border-line pt-3">
          <DriverRow team={homeTeam} form={homeForm} ppg={homePpg} align="start" />
          <AppText variant="micro" weight="medium" className="px-2 text-muted">
            {t('match.recentForm')}
          </AppText>
          <DriverRow team={awayTeam} form={awayForm} ppg={awayPpg} align="end" />
        </View>
      ) : null}

      <AppText variant="micro" className="text-muted">
        {t('match.modelBasis')}
      </AppText>
    </Card>
  );
}
